package com.flowyride.feature.settings.ui

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ExperimentalLayoutApi
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.AlertDialog
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.semantics.selected
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.flowyride.core.designsystem.component.FlowyTextField
import com.flowyride.core.designsystem.component.SavedPlaceBadge
import com.flowyride.core.designsystem.component.SavedPlaceColors
import com.flowyride.core.designsystem.component.savedPlaceColor
import com.flowyride.core.domain.place.SavedPlace
import com.flowyride.core.domain.place.SavedPlaceEdit
import com.flowyride.core.domain.place.SavedPlaceRules
import com.flowyride.feature.settings.resources.Res
import com.flowyride.feature.settings.resources.cancel
import com.flowyride.feature.settings.resources.delete
import com.flowyride.feature.settings.resources.delete_confirm
import com.flowyride.feature.settings.resources.edit_place_title
import com.flowyride.feature.settings.resources.place_color
import com.flowyride.feature.settings.resources.place_emoji
import com.flowyride.feature.settings.resources.place_emoji_custom
import com.flowyride.feature.settings.resources.place_emoji_none
import com.flowyride.feature.settings.resources.place_name
import com.flowyride.feature.settings.resources.save
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

/** A tap away; any other one can be typed below them. */
internal val SuggestedEmojis = listOf("🏠", "🏢", "🏍️", "🔧", "⛽", "☕", "🍽️", "🍺", "🏔️", "🏖️", "🌲", "🏁", "⭐", "❤️", "👥", "📍")

/** Name, emoji and color of a saved address; deleting it too. */
@OptIn(ExperimentalLayoutApi::class)
@Composable
internal fun SavedPlaceEditDialog(
    place: SavedPlace,
    saving: Boolean,
    error: StringResource?,
    onSave: (SavedPlaceEdit) -> Unit,
    onDelete: () -> Unit,
    onDismiss: () -> Unit,
) {
    var name by remember(place.id) { mutableStateOf(place.name) }
    var emoji by remember(place.id) { mutableStateOf(place.emoji) }
    var custom by remember(place.id) { mutableStateOf(place.emoji?.takeIf { it !in SuggestedEmojis }.orEmpty()) }
    var color by remember(place.id) { mutableStateOf(place.color) }
    var confirmDelete by remember { mutableStateOf(false) }
    val edit = SavedPlaceEdit(name.trim(), emoji, color)
    val valid = SavedPlaceRules.validate(edit) == null

    AlertDialog(
        onDismissRequest = onDismiss,
        title = {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(12.dp)) {
                SavedPlaceBadge(emoji, color, size = if (emoji == null) 20.dp else 36.dp)
                Text(stringResource(Res.string.edit_place_title))
            }
        },
        text = {
            Column(Modifier.verticalScroll(rememberScrollState()), verticalArrangement = Arrangement.spacedBy(12.dp)) {
                FlowyTextField(
                    value = name,
                    onValueChange = { name = it.take(SavedPlaceRules.NAME_MAX) },
                    label = stringResource(Res.string.place_name),
                    imeAction = ImeAction.Done,
                    modifier = Modifier.fillMaxWidth(),
                )

                Text(stringResource(Res.string.place_emoji), style = MaterialTheme.typography.labelLarge)
                FlowRow(horizontalArrangement = Arrangement.spacedBy(6.dp), verticalArrangement = Arrangement.spacedBy(6.dp)) {
                    Choice(selected = emoji == null, onClick = { emoji = null; custom = "" }, width = 72.dp) {
                        Text(stringResource(Res.string.place_emoji_none), style = MaterialTheme.typography.labelMedium)
                    }
                    SuggestedEmojis.forEach { option ->
                        Choice(selected = emoji == option, onClick = { emoji = option; custom = "" }) {
                            Text(option, fontSize = 22.sp)
                        }
                    }
                }
                FlowyTextField(
                    value = custom,
                    onValueChange = { typed ->
                        custom = typed.take(SavedPlaceRules.EMOJI_MAX)
                        emoji = custom.trim().ifEmpty { null }
                    },
                    label = stringResource(Res.string.place_emoji_custom),
                    imeAction = ImeAction.Done,
                    modifier = Modifier.fillMaxWidth(),
                )

                Text(stringResource(Res.string.place_color), style = MaterialTheme.typography.labelLarge)
                FlowRow(horizontalArrangement = Arrangement.spacedBy(8.dp), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    SavedPlaceColors.forEach { option ->
                        // The first color is the default: no color stored.
                        val value = option.takeIf { it != SavedPlaceColors.first() }
                        ColorChoice(savedPlaceColor(option), selected = color == value || (value == null && color == SavedPlaceColors.first())) {
                            color = value
                        }
                    }
                }
                error?.let { Text(stringResource(it), color = MaterialTheme.colorScheme.error, style = MaterialTheme.typography.bodyMedium) }
            }
        },
        confirmButton = {
            TextButton(onClick = { onSave(edit) }, enabled = valid && !saving) { Text(stringResource(Res.string.save)) }
        },
        dismissButton = {
            Row {
                TextButton(onClick = { confirmDelete = true }, enabled = !saving) {
                    Text(stringResource(Res.string.delete), color = MaterialTheme.colorScheme.error)
                }
                TextButton(onClick = onDismiss) { Text(stringResource(Res.string.cancel)) }
            }
        },
    )

    if (confirmDelete) {
        AlertDialog(
            onDismissRequest = { confirmDelete = false },
            title = { Text(stringResource(Res.string.delete_confirm, place.name)) },
            confirmButton = {
                TextButton(onClick = {
                    confirmDelete = false
                    onDelete()
                }) { Text(stringResource(Res.string.delete), color = MaterialTheme.colorScheme.error) }
            },
            dismissButton = { TextButton(onClick = { confirmDelete = false }) { Text(stringResource(Res.string.cancel)) } },
        )
    }
}

@Composable
private fun Choice(selected: Boolean, onClick: () -> Unit, width: androidx.compose.ui.unit.Dp = 44.dp, content: @Composable () -> Unit) {
    val shape = RoundedCornerShape(12.dp)
    Box(
        Modifier
            .size(width = width, height = 44.dp)
            .background(if (selected) MaterialTheme.colorScheme.primaryContainer else MaterialTheme.colorScheme.surfaceVariant, shape)
            .border(2.dp, if (selected) MaterialTheme.colorScheme.primary else Color.Transparent, shape)
            .clickable(onClick = onClick)
            .semantics { this.selected = selected },
        contentAlignment = Alignment.Center,
    ) { content() }
}

@Composable
private fun ColorChoice(color: Color, selected: Boolean, onClick: () -> Unit) {
    Box(
        Modifier
            .size(36.dp)
            .border(3.dp, if (selected) MaterialTheme.colorScheme.onSurface else Color.Transparent, CircleShape)
            .padding(5.dp)
            .background(color, CircleShape)
            .clickable(onClick = onClick)
            .semantics { this.selected = selected },
    )
}
